/** @module @liquidefy/code-editor/terminal/terminal-output @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  ProcessedOutput,
  OutputClassification,
  ExtractedEntity,
  EntityType,
  DetectedFormat,
  OutputContent,
  Range,
  Position,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  now,
  classifyOutput as engineClassifyOutput,
  detectFormat as engineDetectFormat,
  extractEntities as engineExtractEntities,
  isErrorOutput as engineIsError,
  isProgressOutput as engineIsProgress,
  FILE_PATH_REGEX,
  URL_REGEX,
  EMAIL_REGEX,
  IP_REGEX,
  PORT_REGEX,
  PID_REGEX,
  GIT_HASH_REGEX,
  UUID_REGEX,
  TIMESTAMP_REGEX,
  ENV_VAR_REGEX,
  ERROR_CODE_REGEX,
  DOCKER_ID_REGEX,
  HOSTNAME_REGEX,
  VERSION_REGEX,
  ANSI_REGEX,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 OUTPUT PROCESSING — processOutput, classifyOutput, status checks
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Process raw terminal output into a classified, entity-extracted structure.
 */
export function processOutput(raw: string, timestamp?: number): ProcessedOutput {
  const ts = timestamp ?? now();
  const stripped = stripANSI(raw);
  const classification = classifyOutput(stripped);
  const entities = extractEntities(stripped);
  const format = detectFormat(stripped);

  const content: OutputContent = {
    text: stripped,
    structured: tryParseStructured(stripped, format),
  };

  return {
    raw,
    content,
    classification,
    entities,
    format: format !== 'plain' ? format : undefined,
    timestamp: ts,
  };
}

/**
 * Classify terminal output by analyzing content patterns.
 */
export function classifyOutput(text: string): OutputClassification {
  if (!text || text.trim().length === 0) return 'command-output';

  if (isErrorOutput(text)) return 'error-message';
  if (isWarningOutput(text)) return 'warning';
  if (isSuccessOutput(text)) return 'success';
  if (isProgressOutput(text)) return 'progress';
  if (isPromptOutput(text)) return 'prompt';

  // Check for log-format lines
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(text)) return 'log';
  // Check for system messages
  if (/^(Starting|Stopping|Restarting|Loading|Initializing|Shutting down)/mi.test(text)) return 'system';
  // Check for data output (JSON, tables, etc.)
  if (text.startsWith('{') || text.startsWith('[') || text.includes('|---')) return 'data';
  // Check for interactive prompts
  if (/\(y\/n\)|press any key|enter to continue|\?\s*$/i.test(text)) return 'interactive';
  // Check for status messages
  if (/^(ok|done|ready|listening|running|up|online|healthy)/mi.test(text)) return 'status';

  return 'command-output';
}

/**
 * Check if the text contains error-like patterns.
 */
export function isErrorOutput(text: string): boolean {
  const errorPatterns = [
    /\berror\b/i,
    /\bfailed\b/i,
    /\bfailure\b/i,
    /\bfatal\b/i,
    /\bcannot\b/i,
    /\bunable to\b/i,
    /\bnot found\b/i,
    /\bno such\b/i,
    /\bpermission denied\b/i,
    /\baccess denied\b/i,
    /\bsegmentation fault\b/i,
    /\bsegfault\b/i,
    /\bcore dumped\b/i,
    /\bpanic\b/i,
    /\bexception\b/i,
    /\btraceback\b/i,
    /\bstack trace\b/i,
    /\bcommand not found\b/i,
    /\bsyntax error\b/i,
    /\btype error\b/i,
    /\breference error\b/i,
    /\bno space left\b/i,
    /\bconnection refused\b/i,
    /\btimeout\b/i,
    /\baborted\b/i,
    /^E\d{4}\b/m,
    /exit code [1-9]\d*/i,
    /exited with\s+[1-9]/i,
  ];
  return errorPatterns.some(p => p.test(text));
}

/**
 * Check if the text contains warning-like patterns.
 */
export function isWarningOutput(text: string): boolean {
  const warnPatterns = [
    /\bwarn(ing)?\b/i,
    /\bdeprecated\b/i,
    /\bdeprecation\b/i,
    /\bcaution\b/i,
    /\bnote:\b/i,
    /\bnotice:\b/i,
    /\bfyi\b/i,
    /\bexperimental\b/i,
    /\bunsafe\b/i,
    /\bunstable\b/i,
  ];
  return warnPatterns.some(p => p.test(text)) && !isErrorOutput(text);
}

/**
 * Check if the text contains success-like patterns.
 */
export function isSuccessOutput(text: string): boolean {
  const successPatterns = [
    /\bsuccess(ful(ly)?)?\b/i,
    /\bcompleted?\b/i,
    /\bfinished\b/i,
    /\bpassed\b/i,
    /\b(all )?tests? passed\b/i,
    /\bbuild succeeded\b/i,
    /\binstalled\b/i,
    /\bupdated\b/i,
    /\bcompiled successfully\b/i,
    /\b✓\b/,
    /\b✔\b/,
    /\bdone[.!]?\s*$/im,
    /\bok[.!]?\s*$/im,
    /exit code 0/i,
    /exited with\s+0/i,
  ];
  return successPatterns.some(p => p.test(text)) && !isErrorOutput(text);
}

/**
 * Check if the text contains progress-like patterns.
 */
export function isProgressOutput(text: string): boolean {
  const progressPatterns = [
    /\d+%/,
    /\[\s*[=>#-]+\s*\]/,
    /\([\d.]+\/[\d.]+\)/,
    /downloading/i,
    /uploading/i,
    /compiling/i,
    /building/i,
    /bundling/i,
    /processing/i,
    /extracting/i,
    /installing/i,
    /fetching/i,
    /cloning/i,
    /loading/i,
    /\bstep\s+\d+\s*(of|\/)\s*\d+/i,
    /\[\d+\/\d+\]/,
    /\.{3,}$/m,
    /⠋|⠙|⠹|⠸|⠼|⠴|⠦|⠧|⠇|⠏/,
  ];
  return progressPatterns.some(p => p.test(text));
}

/**
 * Check if the text looks like a shell prompt.
 */
export function isPromptOutput(text: string): boolean {
  const promptPatterns = [
    /[$#%>]\s*$/m,
    /^\w+@[\w.-]+[:#]/m,
    /^\([\w-]+\)\s*[$#]/m,
    /^PS\s*[\w\\/>]+>\s*$/m,
    /^>>>\s*$/m,
    /^In \[\d+\]:\s*$/m,
    /^irb\([\w:]+\):\d+:\d+>\s*$/m,
  ];
  return promptPatterns.some(p => p.test(text));
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 ENTITY EXTRACTION — extractEntities + individual extractors
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extract all recognizable entities from text.
 */
export function extractEntities(text: string): ExtractedEntity[] {
  const entities: ExtractedEntity[] = [];

  entities.push(...extractFilePaths(text));
  entities.push(...extractURLs(text));
  entities.push(...extractEmails(text));
  entities.push(...extractIPs(text));
  entities.push(...extractPorts(text));
  entities.push(...extractPIDs(text));
  entities.push(...extractGitHashes(text));
  entities.push(...extractUUIDs(text));
  entities.push(...extractTimestamps(text));
  entities.push(...extractEnvVars(text));
  entities.push(...extractDockerIds(text));
  entities.push(...extractVersions(text));
  entities.push(...extractErrorCodes(text));
  entities.push(...extractHostnames(text));

  return entities;
}

/**
 * Extract file paths from text.
 */
export function extractFilePaths(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(FILE_PATH_REGEX.source, 'g'), 'file-path', 0.8);
}

/**
 * Extract URLs from text.
 */
export function extractURLs(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(URL_REGEX.source, 'g'), 'url', 0.95);
}

/**
 * Extract email addresses from text.
 */
export function extractEmails(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(EMAIL_REGEX.source, 'g'), 'email', 0.9);
}

/**
 * Extract IPv4 addresses from text.
 */
export function extractIPs(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(IP_REGEX.source, 'g'), 'ip-address', 0.85);
}

/**
 * Extract port numbers from text.
 */
export function extractPorts(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(PORT_REGEX.source, 'g'), 'port', 0.7);
}

/**
 * Extract process IDs from text.
 */
export function extractPIDs(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(PID_REGEX.source, 'g'), 'pid', 0.75);
}

/**
 * Extract git commit hashes from text.
 */
export function extractGitHashes(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(GIT_HASH_REGEX.source, 'g'), 'git-hash', 0.6);
}

/**
 * Extract UUIDs from text.
 */
export function extractUUIDs(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(UUID_REGEX.source, 'gi'), 'uuid', 0.95);
}

/**
 * Extract timestamps from text.
 */
export function extractTimestamps(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(TIMESTAMP_REGEX.source, 'g'), 'timestamp', 0.85);
}

/**
 * Extract environment variable references from text.
 */
export function extractEnvVars(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(ENV_VAR_REGEX.source, 'g'), 'env-var', 0.7);
}

/**
 * Extract Docker container IDs from text.
 */
export function extractDockerIds(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(DOCKER_ID_REGEX.source, 'g'), 'docker-id', 0.5);
}

/**
 * Extract version strings from text.
 */
export function extractVersions(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(VERSION_REGEX.source, 'g'), 'version', 0.75);
}

/**
 * Extract error codes from text.
 */
export function extractErrorCodes(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(ERROR_CODE_REGEX.source, 'gi'), 'error-code', 0.8);
}

/**
 * Extract hostnames from text.
 */
export function extractHostnames(text: string): ExtractedEntity[] {
  return extractByPattern(text, new RegExp(HOSTNAME_REGEX.source, 'gi'), 'hostname', 0.65);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 ENTITY PATTERNS — re-exported regex constants
// ═══════════════════════════════════════════════════════════════════════════════

// Patterns are re-exported from the engine. Additional patterns specific to output:

export const IP_V4_REGEX = IP_REGEX;
export const IP_V6_REGEX = /\b(?:[0-9a-fA-F]{1,4}:){2,7}[0-9a-fA-F]{1,4}\b/g;

export {
  FILE_PATH_REGEX,
  URL_REGEX,
  EMAIL_REGEX,
  PORT_REGEX,
  PID_REGEX,
  GIT_HASH_REGEX,
  UUID_REGEX,
  TIMESTAMP_REGEX,
  ENV_VAR_REGEX,
  DOCKER_ID_REGEX,
  HOSTNAME_REGEX,
  VERSION_REGEX,
  ERROR_CODE_REGEX,
};

// ═══════════════════════════════════════════════════════════════════════════════
// §5 FORMAT DETECTION — detectFormat, isJSON, isXML, isCSV, etc.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Detect the format of text output.
 */
export function detectFormat(text: string): DetectedFormat {
  const trimmed = text.trim();
  if (!trimmed) return 'plain';

  if (isJSON(trimmed)) return 'json';
  if (isXML(trimmed)) return 'xml';
  if (isYAML(trimmed)) return 'yaml';
  if (isTOML(trimmed)) return 'toml';
  if (isINI(trimmed)) return 'ini';
  if (isCSV(trimmed)) return 'csv';
  if (isTable(trimmed)) return 'table';
  if (isTree(trimmed)) return 'tree';
  if (isKeyValue(trimmed)) return 'key-value';
  if (isLogFormat(trimmed)) return 'log';

  return 'plain';
}

/**
 * Check if text is valid JSON.
 */
export function isJSON(text: string): boolean {
  const trimmed = text.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if text looks like XML/HTML.
 */
export function isXML(text: string): boolean {
  const trimmed = text.trim();
  return (trimmed.startsWith('<?xml') || trimmed.startsWith('<')) &&
    trimmed.endsWith('>') &&
    /<\/\w+>\s*$/s.test(trimmed);
}

/**
 * Check if text looks like CSV data.
 */
export function isCSV(text: string): boolean {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return false;

  // Check if lines have consistent comma-separated fields
  const firstLineCommas = (lines[0]!.match(/,/g) || []).length;
  if (firstLineCommas === 0) return false;

  let consistentCount = 0;
  for (let i = 1; i < Math.min(lines.length, 5); i++) {
    const commas = (lines[i]!.match(/,/g) || []).length;
    if (commas === firstLineCommas) consistentCount++;
  }

  return consistentCount >= Math.min(lines.length - 1, 3);
}

/**
 * Check if text looks like YAML.
 */
export function isYAML(text: string): boolean {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return false;
  if (lines[0]!.trim() === '---') return true;

  let yamlLikeLines = 0;
  for (const line of lines.slice(0, 10)) {
    if (/^\s*[\w.-]+:\s+/.test(line) || /^\s*-\s+/.test(line)) {
      yamlLikeLines++;
    }
  }
  return yamlLikeLines >= Math.min(lines.length, 3);
}

/**
 * Check if text looks like TOML.
 */
export function isTOML(text: string): boolean {
  const lines = text.trim().split('\n');
  let tomlHeaders = 0;
  let tomlAssignments = 0;

  for (const line of lines.slice(0, 20)) {
    if (/^\s*\[[\w.-]+\]\s*$/.test(line)) tomlHeaders++;
    if (/^\s*[\w.-]+\s*=\s*/.test(line)) tomlAssignments++;
  }
  return tomlHeaders >= 1 && tomlAssignments >= 2;
}

/**
 * Check if text looks like INI format.
 */
export function isINI(text: string): boolean {
  const lines = text.trim().split('\n');
  let iniSections = 0;
  let iniAssignments = 0;

  for (const line of lines.slice(0, 20)) {
    if (/^\s*\[[\w\s.-]+\]\s*$/.test(line)) iniSections++;
    if (/^\s*[\w.-]+\s*=\s*/.test(line)) iniAssignments++;
    if (/^\s*[;#]/.test(line)) iniAssignments++; // Comments count as INI-like
  }
  return iniSections >= 1 && iniAssignments >= 1;
}

/**
 * Check if text looks like an ASCII table.
 */
export function isTable(text: string): boolean {
  const lines = text.trim().split('\n');
  if (lines.length < 3) return false;

  let pipeLines = 0;
  let dashLines = 0;
  for (const line of lines.slice(0, 10)) {
    if (line.includes('|') && (line.match(/\|/g) || []).length >= 2) pipeLines++;
    if (/^[\s|+-]+$/.test(line) || /^[\s|─═]+$/.test(line)) dashLines++;
  }
  return pipeLines >= 2 && dashLines >= 1;
}

/**
 * Check if text looks like tree output (e.g., from `tree` command).
 */
export function isTree(text: string): boolean {
  const lines = text.trim().split('\n');
  if (lines.length < 3) return false;

  let treeLikeLines = 0;
  for (const line of lines) {
    if (/^[\s│├└┬─┤]+/.test(line) || /^[\s|`\\+-]+/.test(line)) {
      treeLikeLines++;
    }
  }
  return treeLikeLines >= lines.length * 0.4;
}

/**
 * Check if text looks like key-value pairs.
 */
export function isKeyValue(text: string): boolean {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return false;

  let kvLines = 0;
  for (const line of lines.slice(0, 10)) {
    if (/^\s*[\w.-]+\s*[:=]\s+\S/.test(line)) kvLines++;
  }
  return kvLines >= Math.min(lines.length, 3);
}

/**
 * Check if text looks like structured log output.
 */
export function isLogFormat(text: string): boolean {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return false;

  let logLines = 0;
  for (const line of lines.slice(0, 10)) {
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(line)) logLines++;
    if (/^\[?(INFO|DEBUG|WARN|ERROR|TRACE|FATAL)\]?\s/i.test(line)) logLines++;
  }
  return logLines >= Math.min(lines.length, 2);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 OUTPUT ENRICHMENT — linkify, highlightErrors, summarize, extractStructured, stripANSI
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Wrap URLs and file paths in hyperlink markers.
 */
export function linkify(text: string): string {
  let result = text;
  // Linkify URLs
  result = result.replace(
    new RegExp(URL_REGEX.source, 'g'),
    (match) => `\x1b]8;;${match}\x07${match}\x1b]8;;\x07`
  );
  return result;
}

/**
 * Mark error regions in text with ANSI red color.
 */
export function highlightErrors(text: string): string {
  const errorKeywords = /\b(error|failed|failure|fatal|exception|panic)\b/gi;
  return text.replace(errorKeywords, '\x1b[31m$1\x1b[0m');
}

/**
 * Summarize output by truncating to maxLines.
 */
export function summarizeOutput(text: string, maxLines?: number): string {
  const limit = maxLines ?? 20;
  const lines = text.split('\n');
  if (lines.length <= limit) return text;

  const head = lines.slice(0, Math.floor(limit / 2));
  const tail = lines.slice(-Math.floor(limit / 2));
  const omitted = lines.length - head.length - tail.length;

  return [...head, `... (${omitted} lines omitted) ...`, ...tail].join('\n');
}

/**
 * Extract structured data from text based on detected format.
 */
export function extractStructuredData(
  text: string,
  format: DetectedFormat
): unknown {
  switch (format) {
    case 'json': return tryParseJSON(text);
    case 'csv': return parseCSVData(text);
    case 'key-value': return parseKeyValueData(text);
    default: return null;
  }
}

/**
 * Strip all ANSI escape sequences from text.
 */
export function stripANSI(text: string): string {
  return text.replace(new RegExp(ANSI_REGEX.source, 'g'), '');
}

/**
 * Count words in text.
 */
export function wordCount(text: string): number {
  const stripped = stripANSI(text).trim();
  if (stripped.length === 0) return 0;
  return stripped.split(/\s+/).length;
}

/**
 * Count lines in text.
 */
export function lineCount(text: string): number {
  if (text.length === 0) return 0;
  return text.split('\n').length;
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function extractByPattern(
  text: string,
  pattern: RegExp,
  type: EntityType,
  confidence: number
): ExtractedEntity[] {
  const entities: ExtractedEntity[] = [];
  const lines = text.split('\n');
  let globalOffset = 0;

  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    const line = lines[lineIdx]!;
    let match: RegExpExecArray | null;
    const regex = new RegExp(pattern.source, pattern.flags);

    while ((match = regex.exec(line)) !== null) {
      const value = match[0];
      const col = match.index;

      const entity: ExtractedEntity = {
        type,
        value,
        range: {
          start: { line: lineIdx, column: col },
          end: { line: lineIdx, column: col + value.length },
        },
        confidence,
      };
      entities.push(entity);
    }

    globalOffset += line.length + 1; // +1 for newline
  }

  return entities;
}

function tryParseJSON(text: string): unknown | null {
  try {
    return JSON.parse(text.trim());
  } catch {
    return null;
  }
}

function parseCSVData(text: string): string[][] {
  return text.trim().split('\n').map(line => line.split(',').map(cell => cell.trim()));
}

function parseKeyValueData(text: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of text.trim().split('\n')) {
    const match = line.match(/^\s*([\w.-]+)\s*[:=]\s*(.+?)\s*$/);
    if (match) {
      result[match[1]!] = match[2]!;
    }
  }
  return result;
}

function tryParseStructured(text: string, format: DetectedFormat): unknown | undefined {
  if (format === 'plain') return undefined;
  const result = extractStructuredData(text, format);
  return result ?? undefined;
}
